import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import useCompanies from "../hooks/useCompanies";
import {
  HttpRequestStatus,
  LoadingContainer,
} from "../components/loadingContainer";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { Dropdown } from "primereact/dropdown";
import { Organization } from "../api/models/organization.models";
import ContainerHeader from "../components/containerHeader";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import EditOrganizationDialog from "../dialogs/editOrganizationDialog";
import useCountries from "../hooks/useCountries";
import { Button } from "primereact/button";
import useCompany from "../hooks/useCompany";
import { confirmDialog } from "primereact/confirmdialog";
import useOrganizations from "../hooks/useOrganizations";
import useOrganizationReports from "../hooks/useOrganizationReports";
import { SelectButton } from "primereact/selectbutton";
import { InfoButton } from "../components/infoButton";
import { InfoDot } from "../components/infoDot";
import { useReportingCycleContext } from "../contexts/reportingCycleContext";
import { ReportingCycleEnum } from "../api/models/reportingCycle.models";
import useCreateReportingCycle from "../hooks/useCreateReportingCycle";
import useReportingCycle from "../hooks/useReportingCycle";
import { useCycleItems } from "../utils/cycleUtils";
import TitleComponent from "../components/title";
import { CompanyInvoicingInformation } from "../components/companyInvoicingInformation";
import { SynkkaIdentifiers } from "../components/synkkaIdentifiers";

const OrganizationContainer = styled.div``;

const StyledTable = styled(DataTable)`
  & .edit-organization-link {
    color: var(--sumi-blue);
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: var(--blue-900);
    }
  }

  .p-datatable-tbody > tr > td {
    border: none;
    background-color: #fdfdfd;
    border-bottom: 1px solid #e1e1e1;
    padding: 1.5rem 1rem;
  }

  .p-datatable-thead > tr > th {
    border: none;
    font-weight: 500;
    font-size: 0.8rem;
    color: #888888;
    text-transform: uppercase;
  }

  // rotate svg icon
  & .p-row-toggler svg {
    color: var(--text-color);
    transform: rotate(90deg);
  }

  // rotate 180 degrees when attribute expanded is true
  & .p-row-toggler[aria-expanded="true"] svg {
    transform: rotate(180deg);
  }

  .p-datatable-row-expansion {
    > td {
      padding: 0;
      border: none;
    }

    & .p-datatable-tbody > tr > td {
      background-color: var(--sumi-background-blue);
    }

    & .p-datatable-tbody > tr > td {
      border: none;
    }
  }
`;

const PaddedContainer = styled.div`
  padding: 2rem 3rem 6rem 4rem;
  width: 100%;
  text-align: left;
`;

const StyledDropdown = styled(Dropdown)`
  width: 250px;
  margin: 0.5rem 0;
`;

const CompanySelect = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 40px;

  & label {
    font-size: 0.8rem;
    color: var(--text-color-secondary);
  }
`;

const ReportingTypeSelect = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 40px;
  position: relative;

  & label {
    font-size: 0.8rem;
    color: var(--text-color-secondary);
  }
`;

const WarningText = styled.span`
  margin-top: 5px;
  font-size: 1rem;
  color: var(--sumi-error-color);
  position: absolute;
  bottom: -22px;
  white-space: nowrap;
`;

const YearSelectContainer = styled.div`
  display: flex;
  flex-direction: column;

  label {
    margin-bottom: 0.5rem;
  }
`;

const YearSelectDropdown = styled(Dropdown)`
  width: 200px;
  margin-bottom: 2px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;

const Description = styled.p`
  font-size: 0.9rem;
  max-width: 700px;
  padding: 1rem 2rem 0 4rem;
  text-align: left;
  color: var(--text-color-secondary);
`;

const Organizations: React.FC = () => {
  const { t } = useTranslation();
  const { reportingCycle } = useReportingCycleContext();

  const [selectedActiveCompanyId, setSelectedActiveCompanyId] = useState<
    number | null
  >(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [showWarningTextYear, setShowWarningTextYear] = useState<number | null>(
    null,
  );
  const [reportingType, setReportingType] = useState<
    ReportingCycleEnum | undefined
  >(reportingCycle);
  const [showEditOrganization, setShowEditOrganization] =
    useState<Organization | null>(null);
  const [showNewOrganization, setShowNewOrganization] =
    useState<boolean>(false);
  const [companyId, companyName, organizationId, locale] = useUIStore(
    useShallow((state: UIState) => [
      state.companyId,
      state.companyName,
      state.organizationId,
      state.locale,
    ]),
  );

  const { reportingCycleData, isLoadingReportingCycle } = useReportingCycle({
    companyId: selectedActiveCompanyId as number,
    enabled: !!selectedActiveCompanyId,
  });

  const currentYearReportData = reportingCycleData?.find(
    (cycle) => cycle.year === selectedYear,
  );

  useEffect(() => {
    setReportingType(reportingCycle);
  }, [reportingCycle]);

  // Update reporting type whenever the user changes the selected year
  useEffect(() => {
    if (selectedYear && reportingCycleData) {
      const cycleForYear = reportingCycleData.find(
        (cycle) => cycle.year === selectedYear,
      );
      setReportingType(cycleForYear?.cycle);
    } else if (selectedYear === null) {
      // If year cleared, also clear the reporting type selection
      setReportingType(undefined);
    }
  }, [selectedYear, reportingCycleData]);

  const { companies, isLoadingCompanies } = useCompanies();

  const { createCycle, isCreatingReportingCycle } = useCreateReportingCycle({
    companyId: selectedActiveCompanyId as number,
  });

  const { deleteOrganization, isDeletingOrganization } = useOrganizations();

  const { getOrganizationReports, isLoadingOrganizationReports } =
    useOrganizationReports();

  const { company, isLoadingCompany } = useCompany({
    companyId: selectedActiveCompanyId || companyId,
    enabled: !!selectedActiveCompanyId || !!companyId,
  });

  const contractStartDate = useMemo(() => {
    if (company && company.contractStartDate) {
      return new Date(company.contractStartDate);
    }
    return null;
  }, [company]);

  const contractEndDate = useMemo(() => {
    if (company && company.contractEndDate) {
      return new Date(company.contractEndDate);
    }
    return null;
  }, [company]);

  useEffect(() => {
    if (company && !company.contractStartDate && !company.contractEndDate) {
      setSelectedYear(null);

      return;
    }

    if (company && company.contractStartDate && !company.contractEndDate) {
      const startYear = new Date(company.contractStartDate).getFullYear();
      const currentYear = new Date().getFullYear();
      if (startYear > currentYear) {
        setSelectedYear(null);
      } else {
        setSelectedYear(currentYear);
      }
      return;
    }

    if (company && company.contractStartDate && company.contractEndDate) {
      const startYear = new Date(company.contractStartDate).getFullYear();
      const endYear = new Date(company.contractEndDate).getFullYear();
      const currentYear = new Date().getFullYear();

      if (currentYear < startYear) {
        setSelectedYear(null);
      } else if (currentYear > endYear) {
        setSelectedYear(endYear);
      } else {
        setSelectedYear(currentYear);
      }
    }
  }, [company]);

  const { countries, isLoading: isLoadingCountries } = useCountries();

  useEffect(() => {
    if (company) {
      setSelectedActiveCompanyId(company.id);
    }
  }, [company, setSelectedActiveCompanyId]);

  const isLoading =
    isLoadingCompany || isLoadingCompanies || isLoadingCountries;

  const handleRemove = async (id: number, name: string) => {
    const result = await getOrganizationReports({
      companyId: selectedActiveCompanyId as number,
      organizationId: id,
    });

    if (!!result?.length) {
      alert(t("organization-has-active-reports-cannot-remove"));
    } else {
      confirmDialog({
        showHeader: false,
        message: t("are-you-sure-you-want-remove-organization", {
          name: name,
        }),
        acceptLabel: t("cmd-remove"),
        rejectLabel: t("cmd-cancel"),
        accept: async () => {
          await deleteOrganization(id);
          if (organizationId === id) {
            // If the deleted organization is currently selected, clear it from the UI store
            useUIStore.setState({
              organizationId: null,
              organizationName: null,
            });
          }
        },
        style: { minWidth: "300px" },
      });
    }
  };

  const actionsBodyTemplate = (rowData: Organization) => {
    return (
      <div
        style={{
          display: "flex",
          gap: "1rem",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <span
          role="button"
          tabIndex={0}
          className="edit-organization-link"
          onClick={() => setShowEditOrganization(rowData)}
        >
          {t("cmd-edit-product")}
        </span>
        <span>
          <span
            role="button"
            tabIndex={0}
            style={{ marginLeft: "1rem", cursor: "pointer" }}
            onClick={() => {
              // Implement delete logic here
              handleRemove(rowData.id, rowData.organizationName);
            }}
          >
            {t("cmd-delete-product")}
          </span>
        </span>
      </div>
    );
  };

  const handleReportingTypeChange = (value: ReportingCycleEnum) => {
    confirmDialog({
      showHeader: false,
      message: t("are-you-sure-you-want-change-reporting-cycle", {
        type:
          value === ReportingCycleEnum.YEARLY
            ? t("label-yearly").toLowerCase()
            : value === ReportingCycleEnum.MONTHLY
              ? t("label-monthly").toLowerCase()
              : t("label-quarterly").toLowerCase(),
      }),
      acceptLabel: t("cmd-yes"),
      rejectLabel: t("cmd-no"),
      accept: () => {
        setReportingType(value);
        createCycle({
          companyId: selectedActiveCompanyId as number,
          year: selectedYear as number,
          cycle: value,
        });
        setShowWarningTextYear(null);
      },
      reject: () => {
        // do nothing
      },
      style: { maxWidth: "400px" },
    });
  };

  const reportCycleForSelectedCompany =
    !!reportingCycleData && !!currentYearReportData;

  const items = useCycleItems();

  const yearOptions = useMemo(() => {
    if (!contractStartDate) {
      return [];
    }

    const startYear = contractStartDate.getFullYear();
    const endYear = contractEndDate
      ? contractEndDate.getFullYear()
      : new Date().getFullYear();

    if (startYear > endYear) {
      return [];
    }

    const years: Array<{ label: string; value: number }> = [];
    for (let year = startYear; year <= endYear; year++) {
      years.push({ label: year.toString(), value: year });
    }

    const currentYear = new Date().getFullYear();

    const filteredYears = years.filter((yearObj) => {
      // Only return current and previous years up from 2025
      return yearObj.value <= currentYear && yearObj.value >= 2025;
    });

    return filteredYears;
  }, [contractStartDate, contractEndDate]);

  useEffect(() => {
    const warningYear =
      yearOptions.find((yearOption) => {
        const cycleForYear = reportingCycleData?.find(
          (cycle) => cycle.year === yearOption.value,
        );
        return !cycleForYear;
      })?.value || null;

    setShowWarningTextYear(warningYear);
  }, [yearOptions, reportingCycleData, contractEndDate]);

  return (
    <OrganizationContainer className="organization-view">
      <ContainerHeader
        title={t("header-organizations")}
        description={t("subheader-organizations")}
      />

      <TitleComponent
        text={t("header-organizations-of-company", { company: companyName })}
      />

      <Description>{t("description-organizations")}</Description>

      {(showEditOrganization || showNewOrganization) &&
        selectedActiveCompanyId && (
          <EditOrganizationDialog
            organization={showEditOrganization || undefined}
            show={!!showEditOrganization || showNewOrganization}
            onHide={() => {
              setShowEditOrganization(null);
              setShowNewOrganization(false);
            }}
            companyId={selectedActiveCompanyId}
            countries={countries}
          />
        )}
      <PaddedContainer className="padded-container">
        <LoadingContainer
          status={
            isLoading ? HttpRequestStatus.LOADING : HttpRequestStatus.SUCCESS
          }
        >
          {!companies && <div>{t("label-no-companies-found")}</div>}
          {companies && (
            <div
              style={{
                display: "flex",
                gap: "1rem",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{ display: "flex", gap: "2rem", alignItems: "center" }}
              >
                <CompanySelect>
                  <label>{t("label-active-company")}</label>
                  <StyledDropdown
                    value={selectedActiveCompanyId}
                    options={companies.map((company) => ({
                      label: company.companyName,
                      value: company.id,
                    }))}
                    onChange={(e) => setSelectedActiveCompanyId(e.value)}
                    placeholder={t("placeholder-select-company")}
                  />
                </CompanySelect>
                <ReportingTypeSelect>
                  <label style={{ marginBottom: "5px" }}>
                    {t("label-reporting-cycle")}
                  </label>
                  <Row>
                    <YearSelectContainer>
                      <YearSelectDropdown
                        options={yearOptions}
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(e.value)}
                        placeholder={t("placeholder-select-year")}
                      />
                    </YearSelectContainer>
                    <SelectButton
                      style={{ marginBottom: "5px" }}
                      optionLabel="name"
                      value={reportingType}
                      options={items}
                      onChange={(e) => handleReportingTypeChange(e.value)}
                      disabled={
                        reportCycleForSelectedCompany ||
                        isCreatingReportingCycle ||
                        isLoadingReportingCycle ||
                        !selectedYear
                      }
                    />
                  </Row>
                  {showWarningTextYear && (
                    <WarningText>
                      {t("warning-reporting-cycle", {
                        year: showWarningTextYear,
                      })}
                    </WarningText>
                  )}
                </ReportingTypeSelect>
                <div style={{ marginBottom: "18px", marginLeft: "-1rem" }}>
                  <InfoButton
                    infoText={t("info-reporting-cycle", {
                      year: reportCycleForSelectedCompany
                        ? new Date().getFullYear() + 1
                        : new Date().getFullYear(),
                    })}
                  />
                </div>
              </div>
              <InfoDot
                position="right"
                marginTop="-20px"
                text=""
                content={
                  <div>
                    <p>{t("infotext-suborganization-info-part-1")}</p>
                    <p>{t("infotext-suborganization-info-part-2")}</p>
                    <p>{t("infotext-suborganization-info-part-3")}</p>
                    <p>{t("infotext-suborganization-info-part-4")}</p>
                  </div>
                }
              >
                <Button
                  id="create-organization-button"
                  label={t("cmd-create-organization")}
                  icon="pi pi-plus"
                  onClick={() => setShowNewOrganization(true)}
                  style={{ marginBottom: "20px" }}
                />
              </InfoDot>
            </div>
          )}
          <CompanyInvoicingInformation company={company} />
          <SynkkaIdentifiers
            company={company ?? null}
            isAdmin={false}
            selectedCompanyId={selectedActiveCompanyId || undefined}
          />
          {company && companies && (
            <StyledTable
              value={company?.organizations}
              emptyMessage={t("label-no-organizations")}
              dataKey="id"
              scrollable
            >
              <Column
                field="organizationName"
                header={t("header-organization-name")}
              />
              <Column
                field="raports"
                header={t("header-reports")}
                body={(rowData) => <span>-</span>}
              />
              <Column
                field="created"
                header={t("header-created")}
                body={() => <span>-</span>}
              />

              <Column field="actions" body={actionsBodyTemplate} />
            </StyledTable>
          )}
        </LoadingContainer>
      </PaddedContainer>
    </OrganizationContainer>
  );
};

export default Organizations;
